import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

interface MasterFitness {
  id: string;
  fitnessCode: string;
  fitnessName: string;
  fitnessType: string;
  metricName?: string;
  metricUnit?: string;
  description?: string;
  instructions?: string;
  referenceVideos?: string[];
  areaOfImprovementOptions?: string[];
  suggestedPracticesOptions?: string[];
  difficultyLevel?: number;
  isActive: boolean;
  displayOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface AdminMasterFitnessProps {
  sport?: string;
}

const AdminMasterFitness: React.FC<AdminMasterFitnessProps> = ({ sport = 'Badminton' }) => {
  const [fitness, setFitness] = useState<MasterFitness[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedFitness, setSelectedFitness] = useState<MasterFitness | null>(null);
  const [editingFitness, setEditingFitness] = useState<MasterFitness | null>(null);
  const selectedSport = sport || 'Badminton';

  const [formData, setFormData] = useState({
    fitnessCode: '',
    fitnessName: '',
    fitnessType: '',
    metricName: '',
    metricUnit: '',
    description: '',
    instructions: '',
    referenceVideos: [] as string[],
    areaOfImprovementOptionsText: '',
    suggestedPracticesOptionsText: '',
    difficultyLevel: 1,
    isActive: true,
    displayOrder: 0,
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  };

  const loadFitness = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/master-fitness?sport=${selectedSport}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setFitness(data.data || []);
      } else {
        setError(data.message || 'Failed to load fitness');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load fitness');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFitness();
  }, [selectedSport]);

  const handleAddNew = () => {
    setEditingFitness(null);
    setFormData({
      fitnessCode: '',
      fitnessName: '',
      fitnessType: '',
      metricName: '',
      metricUnit: '',
      description: '',
      instructions: '',
      referenceVideos: [],
      areaOfImprovementOptionsText: '',
      suggestedPracticesOptionsText: '',
      difficultyLevel: 1,
      isActive: true,
      displayOrder: 0,
    });
    setDialogOpen(true);
  };

  const handleEdit = (fitnessItem: MasterFitness) => {
    setEditingFitness(fitnessItem);
    setFormData({
      fitnessCode: fitnessItem.fitnessCode || '',
      fitnessName: fitnessItem.fitnessName || '',
      fitnessType: fitnessItem.fitnessType || '',
      metricName: fitnessItem.metricName || '',
      metricUnit: fitnessItem.metricUnit || '',
      description: fitnessItem.description || '',
      instructions: fitnessItem.instructions || '',
      referenceVideos: fitnessItem.referenceVideos || [],
      areaOfImprovementOptionsText: Array.isArray(fitnessItem.areaOfImprovementOptions) ? fitnessItem.areaOfImprovementOptions.join(', ') : '',
      suggestedPracticesOptionsText: Array.isArray(fitnessItem.suggestedPracticesOptions) ? fitnessItem.suggestedPracticesOptions.join(', ') : '',
      difficultyLevel: fitnessItem.difficultyLevel || 1,
      isActive: fitnessItem.isActive ?? true,
      displayOrder: fitnessItem.displayOrder || 0,
    });
    setDialogOpen(true);
  };

  const handleView = (fitnessItem: MasterFitness) => {
    setSelectedFitness(fitnessItem);
    setViewDialogOpen(true);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        sport: selectedSport,
        ...formData,
        areaOfImprovementOptions: String(formData.areaOfImprovementOptionsText || '')
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0),
        suggestedPracticesOptions: String(formData.suggestedPracticesOptionsText || '')
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0),
        difficultyLevel: formData.difficultyLevel,
        displayOrder: formData.displayOrder,
      };

      const url = editingFitness
        ? `/api/admin/master-fitness/${editingFitness.id}`
        : '/api/admin/master-fitness';
      const method = editingFitness ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        setDialogOpen(false);
        loadFitness();
      } else {
        setError(data.message || 'Failed to save fitness');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save fitness');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this fitness benchmark?')) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/master-fitness/${id}?sport=${selectedSport}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const data = await response.json();
      if (data.success) {
        loadFitness();
      } else {
        setError(data.message || 'Failed to delete fitness');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete fitness');
    } finally {
      setLoading(false);
    }
  };

  const fitnessTypes = ['skipping', 'running', 'wall-situp', 'agility', 'endurance', 'core-strength', 'general'];
  const metricUnits = ['count', 'seconds', 'meters', 'minutes', 'reps', 'sets'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5">Master Fitness Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Sport: {selectedSport}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadFitness}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Add Fitness
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !fitness.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Metric</TableCell>
                <TableCell>Difficulty</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fitness.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="textSecondary" sx={{ py: 3 }}>
                      No fitness benchmarks found. Click "Add Fitness" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                fitness.map((fitnessItem) => (
                  <TableRow key={fitnessItem.id}>
                    <TableCell>{fitnessItem.fitnessCode}</TableCell>
                    <TableCell>{fitnessItem.fitnessName}</TableCell>
                    <TableCell>
                      <Chip label={fitnessItem.fitnessType} size="small" />
                    </TableCell>
                    <TableCell>
                      {fitnessItem.metricName ? `${fitnessItem.metricName} (${fitnessItem.metricUnit || ''})` : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={fitnessItem.difficultyLevel || 1}
                        size="small"
                        color={fitnessItem.difficultyLevel && fitnessItem.difficultyLevel >= 4 ? 'error' : fitnessItem.difficultyLevel && fitnessItem.difficultyLevel >= 3 ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={fitnessItem.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={fitnessItem.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => handleView(fitnessItem)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(fitnessItem)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(fitnessItem.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingFitness ? 'Edit Fitness Benchmark' : 'Add New Fitness Benchmark'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fitness Code *"
                value={formData.fitnessCode}
                onChange={(e) => setFormData({ ...formData, fitnessCode: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fitness Name *"
                value={formData.fitnessName}
                onChange={(e) => setFormData({ ...formData, fitnessName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Fitness Type *</InputLabel>
                <Select
                  value={formData.fitnessType}
                  label="Fitness Type *"
                  onChange={(e) => setFormData({ ...formData, fitnessType: e.target.value })}
                >
                  {fitnessTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Metric Name"
                value={formData.metricName}
                onChange={(e) => setFormData({ ...formData, metricName: e.target.value })}
                placeholder="e.g., Skipping Count, Running Time"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Metric Unit</InputLabel>
                <Select
                  value={formData.metricUnit}
                  label="Metric Unit"
                  onChange={(e) => setFormData({ ...formData, metricUnit: e.target.value })}
                >
                  {metricUnits.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Instructions"
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Area of Improvement (comma separated)"
                value={formData.areaOfImprovementOptionsText}
                onChange={(e) => setFormData({ ...formData, areaOfImprovementOptionsText: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Suggested Practices (comma separated)"
                value={formData.suggestedPracticesOptionsText}
                onChange={(e) => setFormData({ ...formData, suggestedPracticesOptionsText: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Difficulty Level</InputLabel>
                <Select
                  value={formData.difficultyLevel}
                  label="Difficulty Level"
                  onChange={(e) => setFormData({ ...formData, difficultyLevel: e.target.value as number })}
                >
                  <MenuItem value={1}>1 - Beginner</MenuItem>
                  <MenuItem value={2}>2 - Easy</MenuItem>
                  <MenuItem value={3}>3 - Intermediate</MenuItem>
                  <MenuItem value={4}>4 - Advanced</MenuItem>
                  <MenuItem value={5}>5 - Expert</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={formData.displayOrder}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading || !formData.fitnessCode || !formData.fitnessName || !formData.fitnessType}>
            {loading ? <CircularProgress size={20} /> : editingFitness ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Fitness Benchmark Details</DialogTitle>
        <DialogContent>
          {selectedFitness && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">{selectedFitness.fitnessName}</Typography>
                  <Typography variant="body2" color="textSecondary">Code: {selectedFitness.fitnessCode}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Type</Typography>
                  <Chip label={selectedFitness.fitnessType} size="small" />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Difficulty</Typography>
                  <Chip
                    label={selectedFitness.difficultyLevel || 1}
                    size="small"
                    color={selectedFitness.difficultyLevel && selectedFitness.difficultyLevel >= 4 ? 'error' : selectedFitness.difficultyLevel && selectedFitness.difficultyLevel >= 3 ? 'warning' : 'success'}
                  />
                </Grid>
                {selectedFitness.metricName && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Metric</Typography>
                    <Typography variant="body2">
                      {selectedFitness.metricName} ({selectedFitness.metricUnit || 'N/A'})
                    </Typography>
                  </Grid>
                )}
                {selectedFitness.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Description</Typography>
                    <Typography variant="body2">{selectedFitness.description}</Typography>
                  </Grid>
                )}
                {selectedFitness.instructions && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Instructions</Typography>
                    <Typography variant="body2">{selectedFitness.instructions}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip
                    label={selectedFitness.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={selectedFitness.isActive ? 'success' : 'default'}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedFitness && (
            <Button onClick={() => { setViewDialogOpen(false); handleEdit(selectedFitness); }} variant="contained">
              Edit
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminMasterFitness;

